import { p as proxyCustomElement, H, h, F as Fragment, a as Host } from './index.js';
import { d as defineCustomElement$5 } from './p-CkgLRZPl.js';
import { d as defineCustomElement$4 } from './p-aAvJ0uEW.js';
import { d as defineCustomElement$3 } from './p-Birh0vlD.js';
import { d as defineCustomElement$2 } from './p-Bjt5CD4D.js';
import { d as defineCustomElement$1 } from './p-QtR5VUMp.js';
import { d as debounce } from './p-Dg7s1ivP.js';

const scrollIndicatorCss = ":host{--push-top:calc(var(--fade-size-top)/2);--push-bottom:calc(var(--fade-size-bottom)/2);-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;display:flex;flex:1;flex-direction:column;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;max-height:100%;position:relative;width:100%}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}.inner{display:flex;flex:1;flex-direction:column;-webkit-mask-image:linear-gradient(to bottom,#0000 0,#0000 var(--push-top,0),#000 calc(var(--fade-size-top, 0) + var(--push-top, 0)),#000 calc(100% - var(--push-bottom, 0) - var(--fade-size-bottom, 0)),#0000 calc(100% - var(--push-bottom, 0)));mask-image:linear-gradient(to bottom,#0000 0,#0000 var(--push-top,0),#000 calc(var(--fade-size-top, 0) + var(--push-top, 0)),#000 calc(100% - var(--push-bottom, 0) - var(--fade-size-bottom, 0)),#0000 calc(100% - var(--push-bottom, 0)));overflow-x:hidden;overflow-y:auto;scrollbar-width:thin}.tag{left:.125rem;opacity:1;position:absolute;transition:opacity .2s linear,top .25s ease,bottom .25s ease;z-index:1}.tag-up{top:0}.tag-down{bottom:.125rem}:host([data-at-top]) .tag-up{display:none;opacity:0;top:0}:host([data-at-bottom]) .tag-down{bottom:0;display:none;opacity:0}";

const SCROLL_FADE_SIZE = 36;
const SCROLL_TAG_TOLERANCE = 20;
const WuiScrollIndicator = /*@__PURE__*/ proxyCustomElement(class WuiScrollIndicator extends H {
    constructor(registerHost) {
        super();
        if (registerHost !== false) {
            this.__registerHost();
        }
        this.__attachShadow();
        this.handleScroll = ({ currentTarget }) => {
            if (currentTarget !== this.scrollElm) {
                return;
            }
            this.updateElm();
        };
        this.setRef = (elm) => {
            this.scrollElm = elm;
            this.updateElm();
            this.scrollElm?.removeEventListener('scroll', this.handleScroll);
            this.scrollElm?.addEventListener('scroll', this.handleScroll);
        };
        this.isScrollable = false;
        this.showTagsLabel = true;
        this.handleWindowResize = debounce(() => {
            this.updateElm();
        }, 300);
    }
    updateElm() {
        requestAnimationFrame(() => {
            if (!this.scrollElm) {
                return;
            }
            const { scrollHeight, clientHeight, scrollTop } = this.scrollElm;
            this.isScrollable = scrollHeight > clientHeight;
            if (!this.isScrollable) {
                // Remove all classes
                this.elm.style.setProperty('--fade-size-top', '0');
                this.elm.style.setProperty('--fade-size-bottom', '0');
                return;
            }
            const scrollBottom = scrollHeight - clientHeight - scrollTop;
            this.atTop = scrollTop <= SCROLL_TAG_TOLERANCE;
            this.atBottom = scrollBottom <= SCROLL_TAG_TOLERANCE;
            this.elm.style.setProperty('--fade-size-top', `${Math.min(scrollTop, SCROLL_FADE_SIZE)}px`);
            this.elm.style.setProperty('--fade-size-bottom', `${Math.min(scrollBottom, SCROLL_FADE_SIZE)}px`);
        });
    }
    connectedCallback() {
        window.addEventListener('resize', this.handleWindowResize);
    }
    disconnectedCallback() {
        window.removeEventListener('resize', this.handleWindowResize);
    }
    render() {
        const tagLabel = this.showTagsLabel ? 'Scroll for more' : undefined;
        return (h(Host, { key: 'fc00e538fbfbfd4321053990b7451ba21a9f82a6', "data-at-top": this.atTop || undefined, "data-at-bottom": this.atBottom || undefined, "data-tag-centered": this.showTagsLabel ? undefined : true }, h("div", { key: '98ad18a19593c1e2c8266977ab0a23844f6a12a1', part: "inner", class: "inner", ref: this.setRef }, h("slot", { key: '751bec848b402ee3c450b0ccebbe5138894410b0' })), this.isScrollable && (h(Fragment, { key: 'ac133d232546ad6199b64542d7f96f7bcf25eafc' }, h("wui-tag", { key: '99d7e802bd562ccb2d99478b4d195cac56b2e34d', class: "tag tag-up", label: tagLabel, icon: "arrow-up", action: true, onClick: ({ currentTarget }) => {
                this.scrollElm?.scrollTo({ top: 0, behavior: 'instant' });
                currentTarget?.blur();
            } }), h("wui-tag", { key: '9a096eafd344a370243cba668b89882092f1afe4', class: "tag tag-down", label: tagLabel, icon: "arrow-down", action: true, onClick: ({ currentTarget }) => {
                this.scrollElm?.scrollTo({ top: 99999999, behavior: 'instant' });
                currentTarget?.blur();
            } })))));
    }
    get elm() { return this; }
    static get style() { return scrollIndicatorCss; }
}, [257, "wui-scroll-indicator", {
        "showTagsLabel": [4, "show-tags-label"],
        "isScrollable": [32],
        "atTop": [32],
        "atBottom": [32]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-scroll-indicator", "wui-close-button", "wui-icon", "wui-tag", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-scroll-indicator":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiScrollIndicator);
            }
            break;
        case "wui-close-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-tag":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { WuiScrollIndicator as W, defineCustomElement as d };
